import VpnKeyRounded from '@mui/icons-material/VpnKeyRounded';
import React, { useState } from 'react';
import {
  BooleanField,
  Button,
  Create,
  CreateButton,
  Datagrid,
  DateField,
  DeleteButton,
  Edit,
  EditButton,
  ExportButton,
  Filter,
  FormTab,
  List,
  Resource,
  SearchInput,
  SelectInput,
  Show,
  ShowButton,
  TabbedForm,
  TabbedShowLayout,
  Tab,
  TextField,
  TextInput,
  TopToolbar,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
  downloadCSV,
  Confirm,
} from 'react-admin';
import { Stack, Chip, Typography } from '@mui/material';
import { ORG_ID, HOST, BASE_API_URL } from 'config';
import Role from 'constants/role';
import { useHasRole } from 'hooks/useHasRole';
import jsonExport from 'jsonexport/dist';
import { CopyField } from 'shared-fields/CopyField';
import OrganizationReferenceField from 'shared-fields/OrganizationReferenceField';
import OrganizationReferenceInput from 'shared-fields/OrganizationReferenceInput';
import CustomDateInput from 'shared-fields/CustomDateInput';
import { rolesMatch } from 'utils/user';
import { authTokenKey } from 'authProvider';

const exporter = (tokens) => {
  const tokensForExport = tokens.map((token) => ({
    ...token,
    value: `${token.value?.substring(0, 10)}...`, // Mask token values in export
  }));
  jsonExport(tokensForExport, (err, csv) => {
    downloadCSV(csv, 'tokens');
  });
};

function TokenTitle() {
  const record = useRecordContext();
  return (
    <span>
      {record ? `Token ${record.id} | ${record.action}` : ''}
    </span>
  );
}

function TokenFilter(props) {
  return (
    <Filter {...props}>
      <SearchInput source="searchQuery" alwaysOn />
      <SelectInput source="action" alwaysOn />
      <SelectInput
        source="isRevoked"
        choices={[
          { id: false, name: 'Active' },
          { id: true, name: 'Revoked' },
        ]}
        alwaysOn
      />
      {!ORG_ID && <OrganizationReferenceInput alwaysOn />}
    </Filter>
  );
}

function RegenerateTokenButton(props) {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleRegenerateClick = (e) => {
    e.preventDefault();
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setIsLoading(true);

    try {
      const adminToken = localStorage.getItem(authTokenKey);

      const response = await fetch(`${BASE_API_URL}tokens/${record.id}/regenerate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to regenerate token');
      }

      notify('Token regenerated successfully', { type: 'success' });
      refresh();
    } catch (error) {
      notify('Failed to regenerate token', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setConfirmOpen(false);
  };

  return (
    <>
      <Button
        {...props}
        onClick={handleRegenerateClick}
        label={isLoading ? 'Regenerating...' : 'Regenerate'}
        variant="outlined"
        size="small"
        disabled={record?.isRevoked || isLoading}
      />
      <Confirm
        isOpen={confirmOpen}
        title="Regenerate Token"
        content="Are you sure you want to regenerate this token? The old token will become invalid."
        onConfirm={handleConfirm}
        onClose={handleCancel}
      />
    </>
  );
}

function TokenStatusField({ source }) {
  const record = useRecordContext();
  const isRevoked = record?.[source];
  const expireAt = record?.expireAt;
  const isExpired = expireAt && new Date(expireAt) < new Date();

  let status = 'Active';
  let color = 'success';

  if (isRevoked) {
    status = 'Revoked';
    color = 'error';
  } else if (isExpired) {
    status = 'Expired';
    color = 'warning';
  }

  return (
    <Chip
      label={status}
      color={color}
      size="small"
      variant="outlined"
    />
  );
}

function BulkActionButtons() {
  const hasRole = useHasRole();
  return hasRole(Role.SUPER_ADMIN) && <DeleteButton />;
}

export function TokenList(props) {
  const hasRole = useHasRole();

  return (
    <List
      sort={{ field: 'id', order: 'DESC' }}
      filters={<TokenFilter />}
      perPage={25}
      exporter={exporter}
      actions={(
        <TopToolbar>
          {hasRole(Role.SUPER_ADMIN) && <ExportButton />}
          {hasRole(Role.SUPER_ADMIN) && <CreateButton />}
        </TopToolbar>
      )}
      {...props}
    >
      <Datagrid bulkActionButtons={BulkActionButtons}>
        <CopyField source="id" label="ID" />
        <CopyField source="value" label="Value" sx={{ whiteSpace: 'nowrap' }} />
        <TextField source="action" label="Action" />
        <TokenStatusField source="isRevoked" />
        <DateField source="expireAt" label="Expires" showTime />
        {!ORG_ID && <OrganizationReferenceField />}
        <DateField source="createdAt" label="Created" showTime />
        <DateField source="updatedAt" label="Updated" showTime />
        {hasRole(Role.SUPER_ADMIN) && <EditButton />}
        <ShowButton />
      </Datagrid>
    </List>
  );
}

export function TokenEdit(props) {
  return (
    <Edit mutationMode="optimistic" title={<TokenTitle />} {...props}>
      <TabbedForm>
        <FormTab label="General">
          <Stack direction="row" useFlexGap flexWrap="wrap" gap={2}>
            <TextInput source="id" disabled />
            <TextInput
              source="action"
            />
          </Stack>

          <CustomDateInput
            source="expireAt"
            label="Expiration Date"
            sx={{ width: 200 }}
          />

          {!ORG_ID && <OrganizationReferenceInput />}

          <TextInput
            source="value"
            label="Token Value"
            disabled
            rows={3}
            sx={{ width: '100%', fontFamily: 'monospace' }}
          />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
}

export function TokenShow(props) {
  const hasRole = useHasRole();

  return (
    <Show {...props}>
      <TabbedShowLayout>
        <Tab label="General">
          <CopyField source="id" label="ID" />
          <TextField source="action" label="Action" />
          <TokenStatusField source="isRevoked" />
          <DateField source="expireAt" label="Expires" showTime />
          {!ORG_ID && <OrganizationReferenceField />}
          <DateField source="createdAt" label="Created" showTime />
          <DateField source="updatedAt" label="Updated" showTime />

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Token Value
          </Typography>
          <CopyField
            source="value"
            label=""
            sx={{
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              p: 2,
              borderRadius: 1,
              display: 'block',
            }}
          />

          {hasRole(Role.SUPER_ADMIN) && (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <RegenerateTokenButton />
            </Stack>
          )}
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
}

export function TokenCreate(props) {
  return (
    <Create title="Create Token" {...props}>
      <TabbedForm>
        <FormTab label="General">
          <TextInput
            source="action"
            isRequired
            label="Action"
            placeholder="Enter action"
          />

          <CustomDateInput
            source="expireAt"
            label="Expiration Date"
            sx={{ width: 200 }}
            isRequired
          />

          {!ORG_ID && <OrganizationReferenceInput />}
        </FormTab>
      </TabbedForm>
    </Create>
  );
}

export function renderTokenResource(roles) {
  return (
    <Resource
      name="tokens"
      icon={VpnKeyRounded}
      list={TokenList}
      edit={rolesMatch({ roles, role: Role.ORG_ADMIN }) && TokenEdit}
      show={rolesMatch({ roles, role: Role.ORG_ADMIN }) && TokenShow}
      create={rolesMatch({ roles, role: Role.ORG_ADMIN }) && TokenCreate}
    />
  );
}

export const TokenIcon = VpnKeyRounded;
